import { Backdrop, Modal, styled } from "@material-ui/core";
import { motion, Variants } from "framer-motion";
import React from "react";
import useIsMobile from "../../../hooks/use-is-mobile";

interface ModalTransitionProps {
  children?: React.ReactElement | React.ReactElement[];
  in: boolean;
  onEnter?: () => void; // Passed by <Modal>
  onExited?: () => void; // Passed by <Modal>
}

export const CustomModal = styled(Modal)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    top: "var(--header-height) !important" // Move modal under the desktop Header
  }
}));

export const CustomBackdrop = styled(Backdrop)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    top: "calc(var(--header-height))" // Move modal backdrop under the desktop Header
  },
  [theme.breakpoints.down("sm")]: {
    backgroundColor: "rgba(0, 0, 0, 0.75)"
  }
}));

export const CustomModalTransition = React.forwardRef<
  HTMLDivElement,
  ModalTransitionProps
>((props, ref) => {
  const isMobile = useIsMobile();

  const { in: open, children, onEnter, onExited } = props;

  const handleAnimationStart = () => {
    if (open && onEnter) {
      onEnter();
    }
  };

  const handleAnimationComplete = () => {
    if (!open && onExited) {
      onExited();
    }
  };

  const desktopVariants: Variants = {
    initial: {
      opacity: 0.25
    },
    open: {
      opacity: 1,
      transition: { duration: 0.25 }
    },
    exit: {
      opacity: 0.25,
      transition: { duration: 0.25 }
    }
  };

  const mobileVariants: Variants = {
    initial: {
      y: "100%",
      opacity: 0.5
    },
    open: {
      y: "0%",
      transition: { duration: 0.4 },
      opacity: 1
    },
    exit: {
      y: "100%",
      transition: { duration: 0.25 },
      opacity: 0.5
    }
  };

  return (
    <ModalInner
      key={isMobile ? "mobile" : "desktop"}
      ref={ref}
      onAnimationStart={handleAnimationStart}
      onAnimationComplete={handleAnimationComplete}
      initial={"initial"}
      animate={open ? "open" : "exit"}
      variants={isMobile ? mobileVariants : desktopVariants}
    >
      {children}
    </ModalInner>
  );
});

export const ModalInner = styled(motion.div)(({ theme }) => ({
  position: "relative",
  marginLeft: "auto",
  marginRight: "auto",
  backgroundColor: theme.palette.background.default,
  outline: 0,
  height: "var(--100vh)", // Modal takes full height of viewport by default

  [theme.breakpoints.up("md")]: {
    borderRadius: "8px",
    overflow: "hidden",
    marginTop: theme.spacing(4),
    // prettier-ignore
    height: `calc(var(--100vh) - var(--header-height) - (${theme.spacing(4)}px * 2))`, // Leave 32px top and bottom of the modal
    maxWidth: `min(1000px, calc(100vw - ${theme.spacing(4)}px * 2))`, // Leave 32px left and right of the modal, max out at 1000px
    "& .modal-close-button": {
      position: "absolute",
      top: theme.spacing(2.5),
      right: theme.spacing(2.5)
    }
  },
  [theme.breakpoints.down("sm")]: {
    top: "40px",
    height: "calc(var(--100vh) - 40px)",
    borderTopLeftRadius: "12px",
    borderTopRightRadius: "12px",
    "& .modal-close-button": {
      position: "absolute",
      top: theme.spacing(1),
      right: theme.spacing(1)
    }
  },
  [theme.breakpoints.only("sm")]: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1)
  }
}));

export const ModalContent = styled("div")(({ theme }) => ({
  overflowY: "auto",
  height: "100%",
  "& .MuiCircularProgress-root": {
    position: "absolute",
    top: "50%",
    left: "50%",
    margin: "-20px -20px"
  },
  [theme.breakpoints.up("lg")]: {
    paddingTop: theme.spacing(6)
  },
  [theme.breakpoints.up("sm")]: {
    paddingTop: theme.spacing(4)
  },
  [theme.breakpoints.only("xs")]: {
    paddingTop: theme.spacing(3)
  }
}));
